import type { TeamMember } from '@rundown-studio/types'
import { UserRole } from '@rundown-studio/types'
import type { PendingTeamMemberDto } from '@/entities/team'
import { useTeam } from '@/entities/team'
import { useMemo } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
} from '@tanstack/react-table'
import { PendingMemberRoleCell } from '../ui/pending-member-role-cell'
import { TeamMemberEventsSelect } from '../../team-member-events-select'
import { Menu, MenuItem } from '../../../components/interactives/dropdown-menu'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faEllipsis,
  faPaperPlane,
  faTrashCan,
} from '@fortawesome/free-solid-svg-icons'
import { useUser, useDashboardPermission } from '@/entities/user'
import { CreatedAtCell } from '../ui/created-at-cell'

const columnHelper = createColumnHelper<TeamMember & PendingTeamMemberDto>()

type UsePendingMembersTableParams = {
  teamId: string
  onRevokeInvite: (email: string) => void
  onResendInvite: (email: string) => void
}

export const usePendingMembersTable = ({
  teamId,
  onResendInvite,
  onRevokeInvite,
}: UsePendingMembersTableParams) => {
  const { data: user } = useUser()
  const { data: team } = useTeam({
    params: {
      teamId: teamId as string,
    },
  })

  const isTeamAdmin = useDashboardPermission({
    role: UserRole.TEAM_ADMIN,
  })

  const pendingTeamMembers: (TeamMember & PendingTeamMemberDto)[] =
    useMemo(() => {
      if (!team) return []

      return Object.keys(team?.pending).map((email) => ({
        ...team.pending[email],
        email,
      }))
    }, [team])

  const columns = useMemo(
    () => [
      columnHelper.accessor('email', {
        id: 'email',
        cell: ({ getValue }) => {
          return (
            <span className="italic text-gray-500">
              {getValue()} (Invite sent)
            </span>
          )
        },
      }),
      columnHelper.accessor('role', {
        id: 'role',
        header: 'Role',
        cell: (props) => {
          return <PendingMemberRoleCell context={props} teamId={teamId} />
        },
      }),
      columnHelper.accessor('createdAt', {
        id: 'createdAt',
        header: 'Added',
        cell: (props) => {
          return <CreatedAtCell value={props.getValue()} />
        },
      }),
      columnHelper.accessor('eventIds', {
        id: 'events',
        header: 'Events',
        cell: ({ row: { original } }) => {
          if (
            [
              UserRole.EVENT_EDITOR,
              UserRole.EVENT_SHOWCALLER,
              UserRole.EVENT_VIEWER,
            ].includes(original.role)
          ) {
            return <TeamMemberEventsSelect teamId={teamId} member={original} />
          }

          return '-'
        },
      }),
      columnHelper.display({
        id: 'actions',
        cell: ({ row: { original } }) => {
          const isCurrentUser = original.email === user?.email

          return (
            <Menu
              className={
                'px-2 py-1 text-xs rounded-sm disabled:pointer-events-none ring ring-gray-800'
              }
              label={<FontAwesomeIcon icon={faEllipsis} />}
            >
              <>
                <MenuItem
                  icon={faPaperPlane}
                  label="Re-send invite"
                  onClick={() => onResendInvite(original.email)}
                  disabled={!isTeamAdmin} // Non-admins cannot send emails
                />
                <MenuItem
                  icon={faTrashCan}
                  label="Revoke invite"
                  onClick={() => onRevokeInvite(original.email)}
                  disabled={!isTeamAdmin || isCurrentUser} // Non-admins cannot remove members, Current user cannot remove themselves
                />
              </>
            </Menu>
          )
        },
      }),
    ],
    [isTeamAdmin, onResendInvite, onRevokeInvite, teamId, user?.email],
  )

  const pendingTable = useReactTable({
    data: pendingTeamMembers,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })
  const { rows } = pendingTable.getRowModel()

  return {
    pendingMembersRows: rows,
  }
}
